/**
 * The visitor's answer to the cookie banner, in a cookie of its own.
 *
 * The answer is the one thing stored before it is given, so the banner does not
 * ask again on every page, and it is a cookie so the server acts on the same
 * answer: Bindle tells Google and Meta about a step only when it says yes.
 * Raising the version asks everyone again, which a change to what is asked
 * needs.
 *
 * Two answers. Advertising covers Google, Meta and PostHog following a person
 * with recordings; the banner's Accept and Reject set it. Anonymous statistics,
 * PostHog counting pages with nothing kept on the device, run unless the
 * visitor turns them off under Cookie settings, which the law allows since
 * February 2026 as long as turning them off stays that simple.
 */
import { computed, ref } from 'vue'

const COOKIE = 'consent'
const VERSION = 2
const MAX_AGE_SECOND = 31536000

// Note: names the analytics tools set on Bindle's own domain, cleared when a visitor withdraws
const ADVERTISING_STORAGE_PREFIX_LIST = ['_ga', '_gid', '_gcl', '_fbp', '_fbc', 'ph_', '__ph']
const STATISTICS_STORAGE_PREFIX_LIST = ['ph_', '__ph']

function stored() {
  const raw = document.cookie.split('; ').find(cookie => cookie.startsWith(`${COOKIE}=`))

  try {
    const value = raw ? JSON.parse(decodeURIComponent(raw.slice(COOKIE.length + 1))) : null

    return value?.version === VERSION ? value : null
  } catch {
    return null
  }
}

// Note: the first answers were kept in local storage; the cookie replaces them
localStorage.removeItem(COOKIE)

export const consent = ref(stored())
export const consentBannerOpen = ref(consent.value === null)
export const consentSettingsOpen = ref(false)

export const advertisingAllowed = computed(() => consent.value?.advertising === true)
export const statisticsAllowed = computed(() => consent.value?.statistics ?? true)

export function consentDecide({ advertising, statistics = statisticsAllowed.value }) {
  const withdrawnList = [
    ...(advertisingAllowed.value && !advertising ? ADVERTISING_STORAGE_PREFIX_LIST : []),
    ...(statisticsAllowed.value && !statistics ? STATISTICS_STORAGE_PREFIX_LIST : []),
  ]

  consent.value = { advertising, at: new Date().toISOString(), statistics, version: VERSION }
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(consent.value))}; Max-Age=${MAX_AGE_SECOND}; Path=/; SameSite=Lax${secure}`
  consentBannerOpen.value = false
  consentSettingsOpen.value = false

  // Note: a tool already running cannot be unloaded, so the page starts again without it
  if (withdrawnList.length > 0) {
    storageClear(withdrawnList)
    window.location.reload()
  }
}

export function consentSettingsShow() {
  consentBannerOpen.value = true
  consentSettingsOpen.value = true
}

function storageClear(prefixList) {
  const matches = name => prefixList.some(prefix => name.startsWith(prefix))
  const hostParts = window.location.hostname.split('.')
  const domainList = hostParts.map((_, index) => hostParts.slice(index).join('.'))

  for (const cookie of document.cookie.split(';')) {
    const name = cookie.split('=')[0].trim()

    if (!matches(name)) {
      continue
    }
    document.cookie = `${name}=; Max-Age=0; path=/`
    for (const domain of domainList) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=.${domain}`
    }
  }

  for (const name of Object.keys(localStorage)) {
    if (matches(name)) {
      localStorage.removeItem(name)
    }
  }
}
